<script setup lang="ts">
const props = withDefaults(defineProps<{
  rating: number
  size?: 'sm' | 'md' | 'lg'
}>(), { size: 'md' })

const STAR = 'M48,64.7 L62.6,61 L68.7,79.8 L48,64.7 Z M81.6,40.4 L55.9,40.4 L48,16.2 L40.1,40.4 L14.4,40.4 L35.2,55.4 L27.3,79.6 L48.1,64.6 L60.9,55.4 L81.6,40.4 Z'
const EMPTY = '#dcdce6'

const clamped = computed(() => Math.max(0, Math.min(5, Number(props.rating) || 0)))
const displayStars = computed(() => Math.round(clamped.value * 2) / 2)
const label = computed(() => `Rated ${clamped.value.toFixed(1)} out of 5 stars on Trustpilot`)

function tileColor(stars: number) {
  if (stars >= 4.5)
    return '#00b67a'
  if (stars >= 3.5)
    return '#73cf11'
  if (stars >= 2.5)
    return '#ffce00'
  if (stars >= 1.5)
    return '#ff8622'
  return '#ff3722'
}

const tiles = computed(() => {
  const full = Math.floor(displayStars.value)
  const hasHalf = displayStars.value - full >= 0.5
  const color = tileColor(displayStars.value)
  return Array.from({ length: 5 }, (_, i) => {
    if (i < full)
      return { color, half: false }
    if (i === full && hasHalf)
      return { color, half: true }
    return { color: EMPTY, half: false }
  })
})
</script>

<template>
  <svg
    class="tp-stars"
    :data-size="size"
    role="img"
    :aria-label="label"
    viewBox="0 0 528 96"
  >
    <g
      v-for="(t, i) in tiles"
      :key="i"
      class="tp-stars__tile"
      :transform="`translate(${i * 108}, 0)`"
    >
      <template v-if="t.half">
        <rect
          width="48"
          height="96"
          :fill="t.color"
        />
        <rect
          x="48"
          width="48"
          height="96"
          :fill="EMPTY"
        />
      </template>
      <rect
        v-else
        width="96"
        height="96"
        :fill="t.color"
      />
      <path
        :d="STAR"
        fill="#fff"
      />
    </g>
  </svg>
</template>

<style scoped>
.tp-stars {
  --tp-stars-height: 1.5rem;
  display: inline-block;
  height: var(--tp-stars-height);
  width: calc(var(--tp-stars-height) * 528 / 96);
  aspect-ratio: 528 / 96;
  line-height: 0;
  vertical-align: middle;
}
.tp-stars[data-size='sm'] {
  --tp-stars-height: 1.125rem;
}
.tp-stars[data-size='lg'] {
  --tp-stars-height: 2rem;
}

/* Trustpilot's white monochrome star: tile in the text colour, star knocked out. */
.dark-bg .tp-stars__tile rect {
  fill: currentColor;
}
.dark-bg .tp-stars__tile path {
  fill: var(--tp-star-knockout, var(--color-med-deep-navy, #0a2d38));
}
</style>
